// Quiz state management utility for sessionStorage persistence
// This file handles saving and restoring quiz state when the browser is refreshed

export interface QuizSessionState {
  // Quiz identifiers
  quizReference: string;
  quizDetails: any;
  questions: any[];
  
  // Quiz progress state
  currentQuestionIndex: number;
  questionStates: any[];
  isQuizActive: boolean;
  totalTimeRemaining: number;
  lastUpdateTime: number;
  
  // User session
  userSession: any;
  
  // Time extension state
  extensionsUsed: number;
  extensionDialogShown: boolean;
  
  // UI state
  activeFilters: string[];
  
  // Timestamp for state validity
  savedAt: number;
}

const QUIZ_STATE_KEY = 'wizi_quiz_state';
const STATE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Save the current quiz state to sessionStorage
 */
export const saveQuizState = (state: QuizSessionState): void => {
  try {
    const stateWithTimestamp = {
      ...state,
      savedAt: Date.now()
    };
    
    sessionStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(stateWithTimestamp));
    console.log('Quiz state saved to sessionStorage');
  } catch (error) {
    console.error('Failed to save quiz state to sessionStorage:', error);
  }
};

/**
 * Load the quiz state from sessionStorage
 * Returns null if no valid state is found
 */
export const loadQuizState = (quizReference: string): QuizSessionState | null => {
  try {
    const savedStateStr = sessionStorage.getItem(QUIZ_STATE_KEY);
    if (!savedStateStr) {
      return null;
    }
    
    const savedState = JSON.parse(savedStateStr) as QuizSessionState;
    
    // Check if the state is for the current quiz
    if (savedState.quizReference !== quizReference) {
      console.log('Saved state is for a different quiz, clearing it');
      clearQuizState();
      return null;
    }
    
    // Check if the state has expired
    if (Date.now() - savedState.savedAt > STATE_EXPIRY_TIME) {
      console.log('Saved quiz state has expired, clearing it');
      clearQuizState();
      return null;
    }
    
    // Adjust timer based on time elapsed while away
    const timeElapsed = Math.floor((Date.now() - savedState.lastUpdateTime) / 1000);
    if (timeElapsed > 0 && savedState.totalTimeRemaining > 0) {
      // Adjust total time remaining, but don't let it go below 0
      savedState.totalTimeRemaining = Math.max(0, savedState.totalTimeRemaining - timeElapsed);
      
      // Also adjust current question time if it exists
      if (savedState.questionStates && savedState.questionStates[savedState.currentQuestionIndex]) {
        const currentQuestionState = savedState.questionStates[savedState.currentQuestionIndex];
        if (currentQuestionState.timeRemaining > 0) {
          currentQuestionState.timeRemaining = Math.max(0, currentQuestionState.timeRemaining - timeElapsed);
        }
      }
    }
    
    // Update last update time to current time
    savedState.lastUpdateTime = Date.now();
    
    console.log('Quiz state loaded from sessionStorage');
    return savedState;
  } catch (error) {
    console.error('Failed to load quiz state from sessionStorage:', error);
    return null;
  }
};

/**
 * Clear the quiz state from sessionStorage
 */
export const clearQuizState = (): void => {
  try {
    sessionStorage.removeItem(QUIZ_STATE_KEY);
    console.log('Quiz state cleared from sessionStorage');
  } catch (error) {
    console.error('Failed to clear quiz state from sessionStorage:', error);
  }
};

/**
 * Check if there's a saved quiz state for the given quiz reference
 */
export const hasQuizState = (quizReference: string): boolean => {
  try {
    const savedStateStr = sessionStorage.getItem(QUIZ_STATE_KEY);
    if (!savedStateStr) {
      return false;
    }
    
    const savedState = JSON.parse(savedStateStr) as QuizSessionState;
    return savedState.quizReference === quizReference && 
           (Date.now() - savedState.savedAt) <= STATE_EXPIRY_TIME;
  } catch (error) {
    return false;
  }
};

/**
 * Update specific parts of the quiz state without a full save
 * Useful for frequent updates like timer ticks
 */
export const updateQuizStatePartial = (updates: Partial<QuizSessionState>): void => {
  try {
    const savedStateStr = sessionStorage.getItem(QUIZ_STATE_KEY);
    if (savedStateStr) {
      const savedState = JSON.parse(savedStateStr) as QuizSessionState;
      const updatedState = {
        ...savedState,
        ...updates,
        savedAt: Date.now()
      };
      sessionStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(updatedState));
    }
  } catch (error) {
    console.error('Failed to update quiz state in sessionStorage:', error);
  }
};

/**
 * Periodic save function to avoid too frequent sessionStorage writes
 * Should be called on significant state changes, not every timer tick
 */
export const saveQuizStateThrottled = (() => {
  let timeoutId: NodeJS.Timeout | null = null;
  let pendingState: QuizSessionState | null = null;
  
  return (state: QuizSessionState) => {
    pendingState = state;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      if (pendingState) {
        saveQuizState(pendingState);
        pendingState = null;
      }
      timeoutId = null;
    }, 2000); // Save after 2 seconds of no updates
  };
})();
